import React from "react";
import { useTranslation } from "react-i18next";
import brFlag from "../../assets/flags/br.svg";
import usFlag from "../../assets/flags/us.svg";

const LANGUAGES = [
    { code: "pt", label: "PT", name: "Português", flag: brFlag },
    { code: "en", label: "EN", name: "English", flag: usFlag },
] as const;

interface LanguageSwitcherProps {
    /** "dark" => legível sobre o header navy; "light" (default) => sobre fundo claro. */
    variant?: "light" | "dark";
}

export function LanguageSwitcher({ variant = "light" }: LanguageSwitcherProps) {
    const { i18n } = useTranslation();
    // resolvedLanguage falls back to language; strip region (pt-BR -> pt) so the
    // active state still matches when the detector stores a regional variant.
    const current = (i18n.resolvedLanguage ?? i18n.language ?? "pt").split(
        "-",
    )[0];

    const isDark = variant === "dark";

    return (
        <div
            role="group"
            aria-label="Idioma / Language"
            className={`inline-flex items-center gap-0.5 rounded-full border p-0.5 ${
                isDark
                    ? "border-white/25 bg-white/10"
                    : "border-border bg-muted/40"
            }`}
        >
            {LANGUAGES.map((lng) => {
                const active = current === lng.code;
                const activeCls = isDark
                    ? "bg-white text-[hsl(262_60%_40%)] shadow-sm"
                    : "bg-background text-foreground shadow-sm";
                const idleCls = isDark
                    ? "text-white/70 hover:text-white"
                    : "text-muted-foreground hover:text-foreground";
                return (
                    <button
                        key={lng.code}
                        type="button"
                        onClick={() => i18n.changeLanguage(lng.code)}
                        title={lng.name}
                        className={`flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                            active ? activeCls : idleCls
                        }`}
                    >
                        <img
                            src={lng.flag}
                            alt=""
                            aria-hidden
                            className={`h-3.5 w-5 shrink-0 rounded-sm object-contain transition-all ${
                                active ? "" : "opacity-90"
                            }`}
                        />
                        <span>{lng.label}</span>
                    </button>
                );
            })}
        </div>
    );
}
